import { all, call, put, takeLatest, fork } from 'redux-saga/effects';
import { Creators as RecActions, Types as RecTypes } from 'store/ducks/Rec';
import axios from 'axios';
import api from '../../util/Api';
import * as Sentry from '@sentry/react';
import { isUnexpectedError, businessMessage, GENERIC_ERROR } from '../../util/apiError';
import { reportApiError } from '../../util/glitchtip';

function* getRecs({ payload }) {
  try {
    const { status, data } = yield call(api.get, '/recs', {
      params: payload,
    });
    if (status === 200) {
      yield put(RecActions.getRecsSuccess(data));
    } else {
      Sentry.captureMessage('getRecs failed: status not 200', {
        level: 'error',
        extra: { payload, status, data },
      });
      yield put(RecActions.getRecsError());
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(RecActions.getRecsError());
  }
}

function* getRecsImports({ payload }) {
  try {
    const { status, data } = yield call(api.get, '/imports/generation', {
      params: payload,
      timeout: 6000000,
    });
    if (status === 200) {
      yield put(RecActions.getRecsImportSuccess(data));
    } else {
      Sentry.captureMessage('getRecsImports failed: status not 200', {
        level: 'error',
        extra: { payload, status, data },
      });
      yield put(RecActions.getRecsImportError());
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(RecActions.getRecsImportError());
  }
}

function* commitImport({ payload }) {
  try {
    const { status, data } = yield call(api.post, '/imports/commit', payload);
    if (status === 200) {
      yield put(RecActions.commitImportSuccess(data));
    } else {
      Sentry.captureMessage('commitImport failed: status not 200', {
        level: 'error',
        extra: { payload, status, data },
      });
      yield put(RecActions.commitImportError(data));
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      RecActions.commitImportError(error?.response?.data || error?.data || error)
    );
  }
}

function* getRecsBusiness({ payload }) {
  try {
    const { status, data } = yield call(api.get, '/imports/business', {
      params: payload,
    });
    if (status === 200) {
      yield put(RecActions.getRecsBusinessSuccess(data));
    } else {
      Sentry.captureMessage('getRecsBusiness failed: status not 200', {
        level: 'error',
        extra: { payload, status, data },
      });
      yield put(RecActions.getRecsBusinessError());
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(RecActions.getRecsBusinessError());
  }
}

function* postRecs({ payload }) {
  try {
    const { status } = yield call(api.post, '/recs', payload, { skipSentry: true });
    if (status === 200) {
      yield put(RecActions.postRecsSuccess());
    } else {
      Sentry.captureMessage('postRecs failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
      yield put(RecActions.postRecsError({ message: GENERIC_ERROR, unexpected: true }));
    }
  } catch (error) {
    const unexpected = isUnexpectedError(error);
    const reference = yield call(reportApiError, error, {
      flow: 'emissao',
      endpoint: '/recs',
      payload,
      unexpected,
    });
    yield put(
      RecActions.postRecsError({
        message: unexpected ? GENERIC_ERROR : businessMessage(error) || GENERIC_ERROR,
        reference,
        unexpected,
      })
    );
  }
}

function* generationRecs({ payload }) {
  try {
    const { status, data } = yield call(
      api.post,
      '/imports/pre-generation',
      payload
    );
    if (status === 200) {
      yield put(RecActions.generationRecsSuccess());
    } else {
      Sentry.captureMessage('generationRecs failed: status not 200', {
        level: 'error',
        extra: { payload, status, data },
      });
      yield put(RecActions.generationRecsError(data));
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(RecActions.generationRecsError(error?.response?.data));
  }
}

function* putRecs({ payload }) {
  const { form, id } = payload;
  try {
    const { status } = yield call(api.put, `/recs/${id}`, form);
    if (status === 200) {
      yield put(RecActions.putRecsSuccess());
    } else {
      Sentry.captureMessage('putRecs failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
      yield put(RecActions.putRecsError());
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(RecActions.putRecsError());
  }
}

function* approveRec({ payload }) {
  try {
    const { status } = yield call(api.post, `/recs/${payload}/approve`);
    if (status === 200) {
      yield put(RecActions.approveRecSuccess());
    } else {
      Sentry.captureMessage('approveRec failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
      yield put(RecActions.approveRecError());
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(RecActions.approveRecError(error.response.data.message));
  }
}

function* blockRec({ payload }) {
  try {
    const { id, ...body } = payload;
    const { status } = yield call(api.post, `/recs/${id}/block`, body);
    if (status === 200) {
      yield put(RecActions.blockRecSuccess());
    } else {
      Sentry.captureMessage('blockRec failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
      yield put(RecActions.blockRecError());
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(RecActions.blockRecError());
  }
}

function* refuseRec({ payload }) {
  try {
    const { status } = yield call(api.post, `/recs/${payload}/refuse`);
    if (status === 200) {
      yield put(RecActions.refuseRecSuccess());
    } else {
      Sentry.captureMessage('refuseRec failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
      yield put(RecActions.refuseRecError());
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(RecActions.refuseRecError());
  }
}

function* getPDF({ payload }) {
  try {
    const {status, data} = yield api.get(
      `${process.env.REACT_APP_API_URL}/recs/${payload}/pdf`,
      {
        responseType: 'blob',
      }
    );
    if (data) {
      const file = new Blob([data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    }
    if (status === 200) {
      yield put(RecActions.getPdfSuccess());
    } else {
      Sentry.captureMessage('getPDF failed: status not 200', {
        level: 'error',
        extra: { payload, status, data },
      });
      yield put(RecActions.getPdfError());
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
  }
}

function* getRecsWatcher() {
  yield takeLatest(RecTypes.GET_RECS, getRecs);
}

function* getRecsImportWatcher() {
  yield takeLatest(RecTypes.GET_RECS_IMPORT, getRecsImports);
}

function* commitImportWatcher() {
  yield takeLatest(RecTypes.COMMIT_IMPORT, commitImport);
}

function* getRecsBusinessWatcher() {
  yield takeLatest(RecTypes.GET_RECS_BUSINESS, getRecsBusiness);
}

function* postRecsWatcher() {
  yield takeLatest(RecTypes.POST_RECS, postRecs);
}

function* generationRecsWatcher() {
  yield takeLatest(RecTypes.GENERATION_RECS, generationRecs);
}

function* putRecsWatcher() {
  yield takeLatest(RecTypes.PUT_RECS, putRecs);
}

function* approveRecWatcher() {
  yield takeLatest(RecTypes.APPROVE_REC, approveRec);
}

function* blockRecWatcher() {
  yield takeLatest(RecTypes.BLOCK_REC, blockRec);
}

function* refuseRecWatcher() {
  yield takeLatest(RecTypes.REFUSE_REC, refuseRec);
}

function* getPDFWatcher() {
  yield takeLatest(RecTypes.GET_PDF, getPDF);
}

export default function* rootSaga() {
  yield all([
    fork(getRecsWatcher),
    fork(postRecsWatcher),
    fork(generationRecsWatcher),
    fork(putRecsWatcher),
    fork(approveRecWatcher),
    fork(blockRecWatcher),
    fork(refuseRecWatcher),
    fork(getRecsImportWatcher),
    fork(getRecsBusinessWatcher),
    fork(commitImportWatcher),
    fork(getPDFWatcher),
  ]);
}
